const toMg = (v, u) => (u === "mmol/L" ? v * 18 : v);
const fromMg = (v, u) => (u === "mmol/L" ? Math.round((v / 18) * 10) / 10 : Math.round(v));
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export function buildStats(glucose, meals, doses, target, days) {
  const unit = target?.unit || "mg/dL";
  const since = new Date();
  since.setHours(0, 0, 0, 0);
  since.setDate(since.getDate() - (days - 1));
  const inRange = (r) => new Date(r.datetime) >= since;

  const g = glucose.filter(inRange).map((r) => ({ t: new Date(r.datetime).getTime(), mg: toMg(r.value, r.unit) }))
    .sort((a, b) => a.t - b.t);
  const points = g.map((p) => ({ t: p.t, v: fromMg(p.mg, unit) }));
  const n = g.length;
  const mgs = g.map((p) => p.mg);
  const lo = target ? toMg(target.lower, target.unit) : null;
  const hi = target ? toMg(target.upper, target.unit) : null;

  const daysWithReadings = new Set(g.map((p) => dayKey(new Date(p.t)))).size;
  const summary = {
    n, unit,
    avg: n ? fromMg(mgs.reduce((a, b) => a + b, 0) / n, unit) : null,
    min: n ? fromMg(Math.min(...mgs), unit) : null,
    max: n ? fromMg(Math.max(...mgs), unit) : null,
    perDay: n ? Math.round((n / days) * 10) / 10 : 0,
    daysWithReadings,
  };

  const pct = (c) => (n ? Math.round((c / n) * 100) : 0);
  const count = (fn) => mgs.filter(fn).length;
  const below = lo != null ? count((v) => v < lo) : 0;
  const above = hi != null ? count((v) => v > hi) : 0;
  summary.below = { c: below, p: pct(below) };
  summary.above = { c: above, p: pct(above) };
  summary.in = { c: lo != null ? n - below - above : 0, p: lo != null ? pct(n - below - above) : 0 };

  const L = lo ?? 70, H = hi ?? 180;
  const u = (mg) => fromMg(mg, unit);
  const defs = [
    ["vlow", "Muito baixo", `<${u(54)}`, (v) => v < 54, "#A50E0E"],
    ["low", "Baixo", `${u(54)}–${u(L)}`, (v) => v >= 54 && v < L, "#E8453C"],
    ["in", "No alvo", `${u(L)}–${u(H)}`, (v) => v >= L && v <= H, "#137333"],
    ["high", "Alto", `${u(H)}–${u(250)}`, (v) => v > H && v <= Math.max(250, H), "#F9AB00"],
    ["vhigh", "Muito alto", `>${u(Math.max(250, H))}`, (v) => v > Math.max(250, H), "#B06000"],
  ];
  const bands = defs.map(([id, label, range, fn, color]) => {
    const c = count(fn);
    return { id, label, range: `${range} ${unit}`, count: c, pct: pct(c), color };
  });

  const hours = Array.from({ length: 24 }, (_, h) => {
    const vals = g.filter((p) => new Date(p.t).getHours() === h).map((p) => p.mg);
    if (!vals.length) return { h, avg: null, range: null };
    return { h, avg: fromMg(vals.reduce((a, b) => a + b, 0) / vals.length, unit), range: [fromMg(Math.min(...vals), unit), fromMg(Math.max(...vals), unit)] };
  });

  const daily = Array.from({ length: days }, (_, i) => {
    const d = new Date(since);
    d.setDate(since.getDate() + i);
    return { key: dayKey(d), label: d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }), carbs: 0, insulin: 0 };
  });
  const byKey = Object.fromEntries(daily.map((d) => [d.key, d]));
  meals.filter(inRange).forEach((m) => { const d = byKey[dayKey(new Date(m.datetime))]; if (d) d.carbs += Number(m.total_carbs) || 0; });
  doses.filter(inRange).forEach((x) => { const d = byKey[dayKey(new Date(x.datetime))]; if (d) d.insulin += Number(x.units) || 0; });
  daily.forEach((d) => { d.carbs = Math.round(d.carbs); d.insulin = Math.round(d.insulin * 10) / 10; });

  return { summary, points, bands, hours, daily };
}
